import React from 'react'
import { isMobile } from '@mote/helpers/browser'
import { __DEV__ } from '@mote/helpers/constants'
import styled, { css, th } from '@xstyled/styled-components'
import { Spinner } from '../spinner/spinner'
import { Tooltip } from '../tooltip/Tooltip'

const spinnerLoadingColor = {
  primary: 'white',
  'primary-inverted': '#65008a',
  secondary: 'black',
  ghost: 'black',
  link: '#ac0ae8',
  'link-inverted': 'white',
  soft: 'black'
}

const NON_FORWARD_PROPS = [
  'as',
  'pill',
  'variant',
  'size',
  'isOnlyIcon',
  'autoWidth',
  'block'
]

export const Button = React.forwardRef(
  (
    {
      variant = 'primary',
      text,
      icon: _icon,
      htmlType = 'button',
      className = '',
      disabled = false,
      iconPosition = 'left',
      size = 'md',
      loading = false,
      loadingText,
      block = false,
      autoWidth = false,
      pill = false,
      style = {},
      tag,
      testId,
      as,
      showTooltip = false,
      forceShowTooltip = false,
      tooltipProps = {
        distance: 8
      },
      ...props
    },
    ref
  ) => {
    const { disabled: tooltipDisabled = false, ...restTooltipProps } =
      tooltipProps

    const { renderAs, type } = React.useMemo(() => {
      let renderAs = as || tag
      let type = !renderAs ? htmlType : null

      if (props.href) {
        renderAs = 'a'
        type = null
      }

      return { renderAs, type }
    }, [as, tag, htmlType, props.href])

    const isOnlyIcon = React.useMemo(
      () => Boolean(_icon) && !(Boolean(text) || Boolean(loadingText)),
      [_icon, text, loadingText]
    )

    const spinner = React.useMemo(() => {
      return (
        <Spinner
          className='button-loading__spinner'
          color={spinnerLoadingColor[variant] ?? '#ffffff'}
          radius={24}
        />
      )
    }, [variant])

    const icon = React.useMemo(() => {
      return (
        <IconContainer
          iconPosition={iconPosition}
          isOnlyIcon={isOnlyIcon}
          size={size}
          variant={variant}
          className='button-icon'
        >
          {_icon}
        </IconContainer>
      )
    }, [_icon, iconPosition, isOnlyIcon, size, variant])

    return (
      <Root
        ref={ref}
        as={renderAs}
        disabled={disabled || loading}
        variant={variant}
        type={type}
        className={className}
        size={size}
        block={block}
        autoWidth={autoWidth}
        data-testid={testId}
        isOnlyIcon={isOnlyIcon}
        pill={pill}
        style={style}
        {...props}
      >
        <StyledTooltip
          content={props['aria-label']}
          disabled={
            !forceShowTooltip &&
            [isMobile(), tooltipDisabled, disabled, !showTooltip].some(
              flag => flag === true
            )
          }
          {...restTooltipProps}
        >
          {loading && iconPosition === 'left' && <>{spinner}</>}
          {!loading && _icon && iconPosition === 'left' && <>{icon}</>}
          {!isOnlyIcon && (
            <Text
              className='button-text'
              variant={variant}
              size={size}
              $loading={loading}
              iconPosition={iconPosition}
              style={style?.text}
            >
              {loading && loadingText ? loadingText : text}
            </Text>
          )}
          {!loading && _icon && iconPosition === 'right' && <>{icon}</>}
          {loading && iconPosition === 'right' && <>{spinner}</>}
        </StyledTooltip>
      </Root>
    )
  }
)

if (__DEV__) {
  Button.displayName = 'Button'
}

const Root = styled.button.withConfig({
  shouldForwardProp: prop => !NON_FORWARD_PROPS.includes(prop)
})`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  appearance: none;
  user-select: none;
  touch-action: manipulation;
  outline: transparent solid 2px !important;
  outline-offset: 2px;
  border: none;
  width: auto;
  text-decoration: none !important;
  box-sizing: border-box;
  z-index: 1;
  white-space: nowrap;
  vertical-align: middle;
  font-family: 'DM Sans', sans-serif;
  transition: all 150ms ease-in;
  border-radius: ${({ pill }) => (pill ? 99999 : 6)}px;

  &:focus {
    box-shadow: outline-primary;
  }

  ${({ isOnlyIcon, block, autoWidth }) => {
    if (isOnlyIcon) {
      return
    }

    if (block)
      return css`
        width: 100%;
      `

    if (autoWidth)
      return css`
        width: max-content;
      `
  }};

  ${({ size, isOnlyIcon }) => {
    if (isOnlyIcon)
      switch (size) {
        case 'lg':
          return css`
            height: 48px;
            width: 48px;
          `
        case 'xs':
          return css`
            height: 24px;
            width: 24px;
          `
        case 'sm':
          return css`
            height: 32px;
            width: 32px;
          `
        default:
          return css`
            height: 40px;
            width: 40px;
          `
      }

    switch (size) {
      case 'lg':
        return css`
          padding: 0 20px;
          height: 48px;
        `
      case 'xs':
        return css`
          padding: 0 8px;
          height: 24px;
        `
      case 'sm':
        return css`
          padding: 0 12px;
          height: 32px;
        `
      default:
        return css`
          padding: 0 16px;
          height: 40px;
        `
    }
  }};

  ${({ variant }) =>
    variant === 'warning' &&
    css`
      background: ${th.color('red.base')};

      span {
        color: ${th.color('white')};
      }

      &:hover {
        background: ${th.color('red.d1')};
      }

      &:active {
        background: ${th.color('red.d2')};
      }
    `};

  ${({ variant }) =>
    variant === 'primary' &&
    css`
      background: ${th.color('primary.base')};

      span {
        color: ${th.color('white')};
      }

      &:hover {
        background: ${th.color('primary.d1')};
      }

      &:active {
        background: ${th.color('primary.d2')};
      }
    `};

  ${({ variant }) =>
    variant === 'primary-inverted' &&
    css`
      background: ${th.color('primaryAlpha.200')};

      &:hover {
        background: ${th.color('primary.d1')};

        span {
          color: ${th.color('white')};
        }
      }

      &:active {
        background: ${th.color('primary.d2')};

        span {
          color: ${th.color('white')};
        }
      }
    `};

  ${({ variant }) =>
    variant === 'secondary' &&
    css`
      border: 1px solid ${th.color('grey.l2')};
      background: ${th.color('white')};

      &:hover {
        background: ${th.color('grey.l3')};
      }

      &:active {
        border: 1px solid ${th.color('grey.l2')};
        background: ${th.color('grey.l2')};
      }
    `};

  ${({ variant }) =>
    variant === 'secondary-with-antithesis' &&
    css`
      border: 1px solid ${th.color('grey.l2')};
      background: ${th.color('white')};

      &:hover {
        background: ${th.color('primary.d1')};
        border-color: ${th.color('primary.d1')};

        span {
          color: ${th.color('white')};
        }
      }

      &:active {
        background: ${th.color('primary.d2')};
        border: 1px solid ${th.color('primary.d2')};

        span {
          color: ${th.color('white')};
        }
      }
    `};

  ${({ variant }) =>
    variant === 'soft' &&
    css`
      background: ${th.color('grey.l4')};

      &:hover {
        background: ${th.color('grey.l3')};
      }

      &:active {
        background: ${th.color('grey.l2')};
      }
    `};

  ${({ variant }) =>
    variant === 'soft-inverted' &&
    css`
      background: ${th.color('whiteAlpha.500')};

      &:hover {
        background: ${th.color('whiteAlpha.400')};
      }

      &:active {
        background: ${th.color('whiteAlpha.300')};
      }
    `};

  ${({ variant }) =>
    variant === 'ghost' &&
    css`
      background: transparent;

      &:hover {
        background: ${th.color('grey.l4')};
      }

      &:active {
        background: ${th.color('grey.l3')};
      }
    `};

  ${({ variant }) =>
    variant === 'ghost-inverted' &&
    css`
      background: transparent;

      &:hover {
        background: ${th.color('whiteAlpha.500')};
      }

      &:active {
        background: ${th.color('whiteAlpha.400')};
      }
    `};

  ${({ variant }) =>
    variant === 'link' &&
    css`
      padding: 0;
      min-width: unset;
      background: unset;
      height: unset;
      justify-content: flex-start;

      span {
        color: ${th.color('primary.base')};
      }

      &:focus {
        box-shadow: none;
      }

      &:hover,
      &:focus {
        span {
          color: ${th.color('primary.d1')};
        }
      }

      &:active {
        span {
          color: ${th.color('primary.d2')};
        }
      }
    `};

  ${({ variant }) =>
    variant === 'link-inverted' &&
    css`
      padding: 0;
      color: ${th.color('white')};
      min-width: unset;
      background: unset;
      height: unset;
      justify-content: flex-start;

      &:focus {
        box-shadow: none;
      }

      &:hover,
      &:focus {
        span {
          color: ${th.color('whiteAlpha.700')};
        }
      }

      &:active {
        span {
          color: ${th.color('whiteAlpha.500')};
        }
      }
    `};

  ${({ variant }) =>
    variant === 'link-secondary' &&
    css`
      padding: 0;
      color: ${th.color('white')};
      min-width: unset;
      background: unset;
      height: unset;
      justify-content: flex-start;

      span {
        color: ${th.color('black')};
      }

      &:focus {
        box-shadow: none;
      }

      &:hover,
      &:focus {
        span {
          color: ${th.color('primary')};
        }
      }

      &:active {
        span {
          color: ${th.color('primary')};
        }
      }
    `};

  &:focus {
    outline: none;
  }

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.4;

    span {
      cursor: not-allowed;
    }
  }
`

const Text = styled.span`
  font-style: normal;
  font-weight: 500;
  color: white;
  line-height: 120%;
  text-align: center;
  transition: all 150ms ease-in;

  ${({ $loading, iconPosition }) => {
    if (!$loading) return

    switch (iconPosition) {
      case 'left':
        return css`
          margin-left: 8px;
        `
      case 'right':
        return css`
          margin-right: 8px;
        `
      default:
        break
    }
  }};

  ${({ size }) => {
    switch (size) {
      case 'lg':
        return css`
          font-size: ${th.fontSize('lg')};
          line-height: ${th.lineHeight('lg')};
        `
      case 'xs':
        return css`
          font-size: ${th.fontSize('xs')};
          line-height: ${th.lineHeight('xs')};
        `
      case 'sm':
        return css`
          font-size: ${th.fontSize('sm')};
          line-height: ${th.lineHeight('sm')};
        `
      default:
        return css`
          font-size: ${th.fontSize('md')};
          line-height: ${th.lineHeight('md')};
        `
    }
  }};

  ${({ variant }) =>
    variant === 'primary-inverted' &&
    css`
      color: ${th.color('primary.base')};
    `};

  ${({ variant }) =>
    variant === 'secondary' &&
    css`
      color: ${th.color('black')};
    `};

  ${({ variant }) =>
    variant === 'secondary-with-antithesis' &&
    css`
      color: ${th.color('black')};
    `};

  ${({ variant }) =>
    variant === 'soft' &&
    css`
      color: ${th.color('black')};
    `};

  ${({ variant }) =>
    variant === 'soft-inverted' &&
    css`
      color: ${th.color('white')};
    `};

  ${({ variant }) =>
    variant === 'ghost' &&
    css`
      color: ${th.color('black')};
    `};

  ${({ variant }) =>
    variant === 'ghost-inverted' &&
    css`
      color: ${th.color('white')};
    `};
`

const IconContainer = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 150ms ease-in;

  img,
  svg {
    width: 100%;
    height: 100%;
  }

  ${({ iconPosition, isOnlyIcon }) =>
    !isOnlyIcon &&
    iconPosition === 'left' &&
    css`
      margin-right: 8px;
    `};

  ${({ iconPosition, isOnlyIcon }) =>
    !isOnlyIcon &&
    iconPosition === 'right' &&
    css`
      margin-left: 8px;
    `};

  ${({ size }) => {
    switch (size) {
      case 'sm':
        return css`
          height: 16px;
          width: 16px;
          font-size: ${th.fontSize('sm')};
          line-height: ${th.lineHeight('sm')};
        `
      case 'xs':
        return css`
          height: 12px;
          width: 12px;
          font-size: ${th.fontSize('xs')};
          line-height: ${th.lineHeight('xs')};
        `
      case 'lg':
        return css`
          height: 24px;
          width: 24px;
          font-size: ${th.fontSize('lg')};
          line-height: ${th.lineHeight('lg')};
        `
      default:
        return css`
          height: 24px;
          width: 24px;
          font-size: ${th.fontSize('md')};
          line-height: ${th.lineHeight('md')};
        `
    }
  }};

  ${({ variant }) =>
    variant === 'primary-inverted' &&
    css`
      color: ${th.color('primary')};
    `};

  ${({ variant }) =>
    variant === 'secondary' &&
    css`
      color: ${th.color('black')};
    `};

  ${({ variant }) =>
    variant === 'secondary-with-antithesis' &&
    css`
      color: ${th.color('black')};
    `};

  ${({ variant }) =>
    variant === 'soft' &&
    css`
      color: ${th.color('black')};
    `};

  ${({ variant }) =>
    variant === 'soft-inverted' &&
    css`
      color: ${th.color('white')};
    `};

  ${({ variant }) =>
    variant === 'ghost' &&
    css`
      color: ${th.color('black')};
    `};

  ${({ variant }) =>
    variant === 'ghost-inverted' &&
    css`
      color: ${th.color('white')};
    `};
`

export const StyledTooltip = styled(Tooltip)`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .react-tooltip-lite {
    width: max-content !important;
  }
`
